import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import brokerageLogo from "@/assets/align-right-realty-logo-trimmed.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const mainNav = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Buyer", href: "/buy" },
  { label: "Search Homes", href: "/search" },
  { label: "Featured Listings", href: "/listings" },
  { label: "Seller", href: "/sell" },
  { label: "For Sale By Owner", href: "/for-sale-by-owner" },
  { label: "Expired Listings", href: "/expired-listings" },
  { label: "Free Books", href: "/#free-books" },
  { label: "Financial", href: "/financial" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

// Desktop nav drops "Home" (logo links home) and shortens "Featured Listings" → "Listings".
const desktopNav = [
  { label: "About", href: "/about" },
  { label: "Buyer", href: "/buy" },
  { label: "Search Homes", href: "/search" },
  { label: "Listings", href: "/listings" },
  { label: "Seller", href: "/sell" },
  { label: "Financial", href: "/financial" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];


const exploreLabel = "Bored ????  Things to do !!!";

const sellerNav = [
  { label: "Sell Your Home", href: "/sell" },
  { label: "For Sale By Owner", href: "/for-sale-by-owner" },
  { label: "Expired Listings", href: "/expired-listings" },
  { label: "Free Books", href: "/#free-books" },
];

const exploreNav = [
  { label: "Explore NJ", href: "/explore-nj" },
  { label: "Explore FL", href: "/explore-fl" },
];

interface NavigationProps {
  /** When true, nav starts transparent over a hero and turns solid on scroll. */
  transparentOnTop?: boolean;
}

const Navigation = ({ transparentOnTop = false }: NavigationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!transparentOnTop);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sellerOpen, setSellerOpen] = useState(false);
  const location = useLocation();
  const isActivePath = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname === href || location.pathname.startsWith(`${href}/`);

  useEffect(() => {
    if (!transparentOnTop) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOnTop]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const isSolid = scrolled;
  const onDark = transparentOnTop && !isSolid;

  const isExploreActive = exploreNav.some((item) =>
    isActivePath(item.href)
  );

  const linkBase =
    "relative inline-flex items-center h-[1.65em] py-0 leading-[1.65] px-0.5 text-[0.875rem] 2xl:text-[0.9375rem] tracking-[0.06em] 2xl:tracking-[0.07em] uppercase font-semibold whitespace-nowrap transition-colors duration-300 group";


  const getLinkColor = (active: boolean) => {
    if (isSolid) {
      return active ? "text-primary" : "text-foreground hover:text-primary";
    }
    return active ? "text-brand-ivory" : "text-brand-ivory/85 hover:text-brand-ivory";
  };

  const underlineColor = onDark ? "bg-brand-ivory/60" : "bg-primary/60";
  const underlineInset = "left-2.5 right-2.5";


  return (
    <>
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          isSolid
            ? "bg-background/95 backdrop-blur-md border-b border-border"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto max-w-[1400px] px-6">
          <div className="flex flex-col justify-center gap-1 md:gap-2 py-3 md:py-4">
            {/* Top row: logo + CTA / mobile toggle */}
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link
                to="/"
                aria-label="John Mastroianni | Align Right Realty - Homekey home"
                className="flex items-end gap-3 shrink-0"
              >
                <img
                  src={brokerageLogo}
                  alt="Align Right Realty - Homekey"
                  width={1080}
                  height={494}
                  className="h-[3.8rem] md:h-[4.5rem] w-auto drop-shadow-sm"
                />
                <div className="hidden sm:flex flex-col leading-none mb-[8.5px] md:mb-[10.5px]">
                  <span
                    className={`font-serif text-[1.35rem] md:text-[1.65rem] font-medium tracking-tight transition-colors duration-300 ${
                      onDark ? "text-brand-ivory" : "text-foreground"
                    }`}
                  >
                    John Mastroianni
                  </span>
                  <span
                    className={`text-[11px] md:text-[12px] tracking-[0.3em] uppercase mt-1 font-semibold whitespace-nowrap transition-colors duration-300 ${
                      onDark ? "text-brand-ivory/80" : "text-primary"
                    }`}
                  >
                    Align Right Realty - Homekey
                  </span>
                </div>

              </Link>

              {/* CTA */}
              <div className="hidden xl:flex items-center gap-4">
                <a
                  href="tel:+16093062926"
                  className={`inline-flex items-center h-[1.65em] py-0 leading-[1.65] gap-2 text-[1.125rem] 2xl:text-[1.1875rem] font-normal tracking-[0.06em] border px-3 shrink-0 transition-all duration-300 ${
                    isSolid
                      ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      : "border-brand-ivory/40 text-brand-ivory hover:bg-brand-ivory hover:text-foreground"
                  }`}
                >
                  <Phone className="h-4 w-4" />
                  (609) 306-2926
                </a>
              </div>

              {/* Mobile Menu Toggle (below xl) */}
              <button
                className={`xl:hidden p-2 justify-self-end transition-colors duration-300 ${
                  isSolid ? "text-foreground" : "text-brand-ivory"
                }`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>

            {/* Bottom row: desktop nav links + Explore dropdown */}
            <div className="hidden xl:flex flex-wrap items-center justify-center gap-x-0.5 xl:gap-x-1 2xl:gap-x-3 gap-y-1">
              {desktopNav.map((item) => {
                if (item.href === "/sell") {
                  const sellerActive = sellerNav.some((s) => isActivePath(s.href));
                  return (
                    <div key={item.href} className="flex items-center gap-x-0.5 xl:gap-x-1 2xl:gap-x-3">
                      <DropdownMenu open={sellerOpen} onOpenChange={setSellerOpen}>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`${linkBase} inline-flex items-center gap-1.5 ${getLinkColor(sellerActive)}`}
                            aria-haspopup="menu"
                            aria-expanded={sellerOpen}
                          >
                            {item.label}
                            <ChevronDown
                              className={`h-3.5 w-3.5 transition-transform duration-300 ${sellerOpen ? "rotate-180" : ""}`}
                            />
                            <span
                              className={`absolute ${underlineInset} bottom-1 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${underlineColor}`}
                            />
                            {sellerActive && (
                              <span className={`absolute ${underlineInset} bottom-1 h-px ${underlineColor}`} />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="center"
                          sideOffset={12}
                          className="min-w-[220px] bg-background border border-border shadow-lg p-2"
                        >
                          {sellerNav.map((s) => (
                            <DropdownMenuItem key={s.href} asChild>
                              <Link
                                to={s.href}
                                className={`w-full text-[11px] tracking-[0.18em] uppercase font-medium py-3 px-4 cursor-pointer transition-colors duration-200 ${
                                  isActivePath(s.href) ? "text-primary" : "text-foreground hover:text-primary"
                                }`}
                              >
                                {s.label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <span
                        aria-hidden="true"
                        className={`select-none text-[1rem] 2xl:text-[1.125rem] font-bold leading-[1.65] ${
                          isSolid ? "text-foreground/80" : "text-brand-ivory/80"
                        }`}
                      >
                        /
                      </span>
                    </div>
                  );
                }
                const active = isActivePath(item.href);
                return (
                  <div key={item.href} className="flex items-center gap-x-0.5 xl:gap-x-1 2xl:gap-x-3">
                    <Link
                      to={item.href}
                      className={`${linkBase} ${getLinkColor(active)}`}
                    >
                      {item.label}
                      <span
                        className={`absolute ${underlineInset} bottom-1 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${underlineColor}`}
                      />
                      {active && (
                        <span
                          className={`absolute ${underlineInset} bottom-1 h-px ${underlineColor}`}
                        />
                      )}
                    </Link>
                    <span
                      aria-hidden="true"
                      className={`select-none text-[1rem] 2xl:text-[1.125rem] font-bold leading-[1.65] ${
                        isSolid ? "text-foreground/80" : "text-brand-ivory/80"
                      }`}
                    >
                      /
                    </span>
                  </div>
                );
              })}


              {/* Explore Dropdown */}
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`${linkBase} inline-flex items-center gap-1.5 ${getLinkColor(
                      isExploreActive
                    )}`}
                    aria-haspopup="menu"
                    aria-expanded={dropdownOpen}
                  >
                    {exploreLabel}
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-300 ${
                        dropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                    <span
                      className={`absolute ${underlineInset} bottom-1 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${underlineColor}`}
                    />
                    {isExploreActive && (
                      <span
                        className={`absolute ${underlineInset} bottom-1 h-px ${underlineColor}`}
                      />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={12}
                  className="min-w-[200px] bg-background border border-border shadow-lg p-2"
                >
                  {exploreNav.map((item) => {
                    const active = isActivePath(item.href);
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link
                          to={item.href}
                          className={`w-full text-[11px] tracking-[0.18em] uppercase font-medium py-3 px-4 cursor-pointer transition-colors duration-200 ${
                            active
                              ? "text-primary"
                              : "text-foreground hover:text-primary"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 xl:hidden">

          <div
            className="absolute inset-0 bg-brand-charcoal/90 backdrop-blur-md"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-[5rem] left-0 right-0 bottom-0 bg-background overflow-y-auto">
            <div className="container mx-auto max-w-[1400px] px-6 py-12">
              <div className="space-y-10">
                <div className="space-y-2">
                  {mainNav.map((item) => {
                    const active = isActivePath(item.href);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`block py-3 text-lg tracking-[0.2em] uppercase font-light transition-colors duration-300 ${
                          active
                            ? "text-primary"
                            : "text-foreground hover:text-primary"
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>

                <div className="border-t border-border pt-8">
                  <p className="eyebrow-sm mb-5">
                    {exploreLabel}
                  </p>
                  <div className="space-y-3">
                    {exploreNav.map((item) => {
                      const active = isActivePath(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`block text-sm tracking-[0.2em] uppercase transition-colors duration-300 ${
                            active
                              ? "text-primary"
                              : "text-foreground/80 hover:text-primary"
                          }`}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-border pt-8">
                  <a
                    href="tel:+16093062926"
                    onClick={() => setIsOpen(false)}
                    className="inline-flex items-center gap-3 text-lg font-normal tracking-[0.06em] border border-primary px-6 py-4 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                  >
                    <Phone className="h-4 w-4" />
                    (609) 306-2926
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
